<template>
  <b-modal
    :active.sync="isModalActive"
    :width="960"
    scroll="keep"
    @close="onModalClose"
  >
    <div class="weekly-admin-assignments-modal">
      <div class="weekly-admin-assignments-modal__header">
        <h2 class="weekly-admin-assignments-modal__title">
          {{ $t('edit_planned_quantities') }}
        </h2>
        <p v-if="contextLabel" class="weekly-admin-assignments-modal__subtitle">
          {{ contextLabel }}
        </p>
      </div>

      <div v-if="isLoading" class="weekly-admin-assignments-modal__loading">
        <b-loading :active="true" :is-full-page="false" />
      </div>

      <div v-else class="weekly-admin-assignments-modal__section">
        <div class="weekly-admin-assignments-modal__section-head">
          <div class="weekly-admin-assignments-modal__section-title">
            <b-icon pack="fas" icon="ruler-combined" size="is-small" class="mr-1" />
            {{ $t('weekly_edit_activity_linked_measurements') }}
          </div>
          <div class="weekly-admin-assignments-modal__legend">
            <span class="weekly-admin-assignments-modal__legend-item">
              <b-icon pack="fas" icon="calendar-alt" size="is-small" class="weekly-admin-assignments-modal__legend-icon--primavera" />
              {{ $t('weekly_edit_activity_measurement_primavera') }}
            </span>
            <span class="weekly-admin-assignments-modal__legend-item">
              <b-icon pack="fas" icon="ruler-combined" size="is-small" class="weekly-admin-assignments-modal__legend-icon--engineering" />
              {{ $t('weekly_edit_activity_measurement_engineering') }}
            </span>
          </div>
        </div>

        <div class="weekly-admin-assignments-modal__table-wrap overflow-x-auto">
          <table class="weekly-admin-assignments-modal__table w-full text-xs border-collapse min-w-max">
            <thead>
              <tr>
                <th class="weekly-admin-assignments-modal__th weekly-admin-assignments-modal__th--slot" />
                <th class="weekly-admin-assignments-modal__th">{{ $t('code') }}</th>
                <th class="weekly-admin-assignments-modal__th weekly-admin-assignments-modal__th--left">{{ $t('name') }}</th>
                <th class="weekly-admin-assignments-modal__th weekly-admin-assignments-modal__th--qty">{{ $t('quantity') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!measurementRows.length">
                <td colspan="4" class="weekly-admin-assignments-modal__td weekly-admin-assignments-modal__td--empty">
                  {{ $t('no_results') }}
                </td>
              </tr>
              <tr
                v-for="(item, index) in measurementRows"
                :key="item.id != null ? item.id : index"
                class="weekly-admin-assignments-modal__row"
              >
                <td class="weekly-admin-assignments-modal__td text-center">
                  <b-icon
                    pack="fas"
                    :icon="item.isImported ? 'calendar-alt' : 'ruler-combined'"
                    size="is-small"
                    :class="item.isImported
                      ? 'weekly-admin-assignments-modal__slot-icon--primavera'
                      : 'weekly-admin-assignments-modal__slot-icon--engineering'"
                  />
                </td>
                <td class="weekly-admin-assignments-modal__td whitespace-nowrap">{{ item.code || '—' }}</td>
                <td class="weekly-admin-assignments-modal__td">{{ item.name || '—' }}</td>
                <td class="weekly-admin-assignments-modal__td">
                  <div class="weekly-admin-assignments-modal__qty-field">
                    <b-numberinput
                      v-model="item.quantity"
                      :min="0"
                      controls-position="compact"
                      size="is-small"
                      icon-pack="fas"
                    />
                    <span v-if="item.unit" class="weekly-admin-assignments-modal__qty-unit">{{ item.unit }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="weekly-admin-assignments-modal__footer">
        <button
          type="button"
          class="weekly-admin-assignments-modal__cancel-btn"
          :disabled="isSaving"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="weekly-admin-assignments-modal__save-btn"
          :disabled="isSaving || isLoading"
          @click="save"
        >
          {{ $t('save') }} {{ $t('quantity') }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import EventBus from '~/plugins/bus'

function mapAssignmentRow(assignment) {
  const total = assignment.total != null && assignment.total !== ''
    ? parseFloat(assignment.total)
    : parseFloat(assignment.scheduled_quantity)
  return {
    id: assignment.id,
    code: assignment.code || '',
    name: assignment.name || assignment.description || '',
    unit: assignment.unit_name || assignment.unit || '',
    quantity: Number.isNaN(total) ? 0 : total,
    originalQuantity: Number.isNaN(total) ? 0 : total,
    isImported: assignment.boq_root_source === 'imported',
  }
}

export default {
  name: 'weekly-admin-assignments-modal',

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      isSaving: false,
      subactivityId: null,
      subactivity: null,
      parentActivity: null,
      measurementRows: [],
    }
  },

  computed: {
    contextLabel() {
      if (!this.subactivity && !this.parentActivity) {
        return ''
      }
      const subName = this.subactivity && this.subactivity.name ? this.subactivity.name : '—'
      const activityName = this.parentActivity && this.parentActivity.name ? this.parentActivity.name : '—'
      return `${this.$t('subactivity')}: ${subName} · ${this.$t('activity')}: ${activityName}`
    },
  },

  methods: {
    show({ subactivity = null, parentActivity = null } = {}) {
      if (!subactivity || subactivity.id == null) {
        return
      }
      this.subactivity = subactivity
      this.parentActivity = parentActivity
      this.subactivityId = subactivity.id
      this.isModalActive = true
      this.loadAssignments()
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      this.subactivityId = null
      this.subactivity = null
      this.parentActivity = null
      this.measurementRows = []
      this.isLoading = false
      this.isSaving = false
    },

    async loadAssignments() {
      if (!this.subactivityId) {
        return
      }

      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/v2/activity/${this.subactivityId}/assignments`, {
          params: { with_boq_root: 1 },
        })

        const assignments = data && data.success ? data.data : []
        this.measurementRows = Array.isArray(assignments)
          ? assignments
            .filter((item) => !item.type || item.type === 'resource')
            .map((item) => mapAssignmentRow(item))
          : []
      } catch (error) {
        console.error('[weekly-admin-assignments-modal] load error:', error)
        this.$notify.error('error_loading_activity_data')
        this.close()
      } finally {
        this.isLoading = false
      }
    },

    buildChangedAssignmentsPayload() {
      return this.measurementRows
        .filter((item) => item.id != null && parseFloat(item.quantity) !== parseFloat(item.originalQuantity))
        .map((item) => ({
          id: item.id,
          total_units: parseFloat(item.quantity),
        }))
    },

    notifyFrontendRefresh() {
      EventBus.$emit('refreshActivitiesNotifications', true)
      EventBus.$emit('refreshAssignmentChangedNotifications', {
        activityId: this.subactivityId,
      })
    },

    async save() {
      if (this.isSaving || !this.subactivityId) {
        return
      }

      const changedAssignments = this.buildChangedAssignmentsPayload()
      if (!changedAssignments.length) {
        this.close()
        return
      }

      this.isSaving = true
      try {
        const { data } = await axios.put(
          `/api/v2/assignments/edit/activity/${this.subactivityId}`,
          { assignment_resources: changedAssignments }
        )

        if (!data || !data.success) {
          this.$notify.error(
            (data && data.message) ? data.message : this.$t('assignment_update_error')
          )
          return
        }

        this.$notify.success('success_editing_activity')
        this.notifyFrontendRefresh()
        this.$emit('saved', {
          activity_id: this.subactivityId,
          parent_id: this.parentActivity && this.parentActivity.id != null
            ? this.parentActivity.id
            : null,
        })
        this.close()
      } catch (error) {
        console.error('[weekly-admin-assignments-modal] save error:', error)
        this.$notify.error(this.$t('assignment_update_error'))
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
.weekly-admin-assignments-modal {
  padding: 1rem 1.25rem 1.25rem;
  background-color: #fff;
}

.weekly-admin-assignments-modal__header {
  margin-bottom: 1rem;
}

.weekly-admin-assignments-modal__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #3b66bc;
}

.weekly-admin-assignments-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #6b7280;
}

.weekly-admin-assignments-modal__loading {
  position: relative;
  min-height: 10rem;
}

.weekly-admin-assignments-modal__section {
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  overflow: hidden;
}

.weekly-admin-assignments-modal__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background-color: #f3f4f6;
  border-bottom: 1px solid #d1d5db;
}

.weekly-admin-assignments-modal__section-title {
  display: flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #374151;
}

.weekly-admin-assignments-modal__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.625rem;
  font-weight: 600;
  color: #6b7280;
}

.weekly-admin-assignments-modal__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.weekly-admin-assignments-modal__legend-icon--primavera {
  color: #7c3aed;
}

.weekly-admin-assignments-modal__legend-icon--engineering {
  color: #2563eb;
}

.weekly-admin-assignments-modal__table-wrap {
  background-color: #fff;
}

.weekly-admin-assignments-modal__th {
  padding: 0.5rem 0.625rem;
  text-align: center;
  font-weight: 600;
  color: #fff;
  background-color: #5b80be;
  border: 1px solid #5b80be;
  white-space: nowrap;
}

.weekly-admin-assignments-modal__th--left {
  text-align: left;
}

.weekly-admin-assignments-modal__th--qty {
  min-width: 9rem;
}

.weekly-admin-assignments-modal__th--slot {
  width: 2.5rem;
}

.weekly-admin-assignments-modal__td {
  padding: 0.5rem 0.625rem;
  border: 1px solid #e5e7eb;
  vertical-align: middle;
  background-color: #fff;
}

.weekly-admin-assignments-modal__td--empty {
  text-align: center;
  color: #6b7280;
  padding: 1.5rem;
}

.weekly-admin-assignments-modal__slot-icon--primavera {
  color: #7c3aed;
}

.weekly-admin-assignments-modal__slot-icon--engineering {
  color: #2563eb;
}

.weekly-admin-assignments-modal__qty-field {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  justify-content: flex-end;
}

.weekly-admin-assignments-modal__qty-unit {
  font-size: 0.6875rem;
  color: #6b7280;
  white-space: nowrap;
}

.weekly-admin-assignments-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.weekly-admin-assignments-modal__cancel-btn,
.weekly-admin-assignments-modal__save-btn {
  padding: 0.375rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-admin-assignments-modal__cancel-btn {
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #374151;
}

.weekly-admin-assignments-modal__cancel-btn:hover:not(:disabled) {
  background-color: #f9fafb;
}

.weekly-admin-assignments-modal__save-btn {
  border: 1px solid #3b66bc;
  background-color: #3b66bc;
  color: #fff;
}

.weekly-admin-assignments-modal__save-btn:hover:not(:disabled) {
  background-color: #2f5299;
}

.weekly-admin-assignments-modal__save-btn:disabled,
.weekly-admin-assignments-modal__cancel-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
